import React, { useEffect, useState } from "react";
import { Box, Divider, Stack, SxProps } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { SurveyBySlugRoot } from "../../../types/SurveyBySlug.types";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  resetResponseState,
  setIsParticipant,
  setParticipantLang,
  setPhaseList,
  setResponseIsEditable,
  setSelectedPhase,
  setSurveyAttemptId,
} from "../../../Redux/reducers/responseSlice";
import { PhasesList } from "../../Responses/types";
import { handlePhaseStatus } from "../../Responses/utils";
import { getFormData } from "../../../Redux/actions/responseAction";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import PhaseList from "./PhaseList";
import QuestionItemWrapper from "../../Responses/QuestionItemWrapper";
import ConfirmationModalWrapper from "../../Responses/ConfirmationModalWrapper";
import CalculationWrapper from "../../Responses/CalculationWrapper/CalculationWrapper";
import { SurveyHeader } from "./SurveyHeader";
import MobileDrawer from "./MobileDrawer";

const rootResponses: SxProps = {
  height: "calc(100% - 80px)",
  width: "100%",
  p: { xs: 0, md: 2 },
  overflow: "auto",
  position: "relative",
  alignItems: "flex-start",
};

const Survey = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedPhase = useAppSelector((state) => state.response.selectedPhase);

  const [isLoading, setIsLoading] = useState(true);
  const [packageName, setPackageName] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const { id: studyId, surveyId } = useParams();

  useEffect(() => {
    const fetchStudyPhases = async () => {
      try {
        setIsLoading(true);

        if (surveyId) {
          const lang = localStorage.getItem("lang") || "";
          if (lang && lang !== "en") {
            dispatch(setParticipantLang(lang));
          }
          dispatch(setIsParticipant(true));
          dispatch(setSurveyAttemptId({ surveyAssignmentId: surveyId }));
          const { data } = await http.get<SurveyBySlugRoot>(
            `/study/${studyId}/participant-survey-responses/${surveyId}`
          );

          if (data?.data?.isConsentRequired) {
            navigate(
              `/sign/${data?.data?.consent?.consentId}?versionId=${data?.data?.consent?.versionId}&studyId=${studyId}&epro=true`
            );
            return;
          }

          dispatch(setResponseIsEditable(true));
          setPackageName(data?.data.package.name);

          let phases: PhasesList[] = [];

          data?.data.package.surveyLinks.forEach((link) => {
            if (link.phase) {
              const phaseForms = link.phase.phaseForms
                .map((phaseForm) => ({
                  position: phaseForm.position,
                  id: phaseForm.form.id,
                  name: phaseForm.form.name,
                  attempt: phaseForm.form.formAttempts?.[0]?.id,
                  status: phaseForm.form.formAttempts?.[0]?.status || "active",
                  phaseName: link.phase.name,
                  phaseId: link.phase.id,
                  locked: false,
                  lockedBy: "",
                  lockedAt: "",
                  isSigned: false,
                  signedBy: "",
                  signedAt: "",
                }))
                .sort((a, b) => a.position - b.position);

              const phaseStatus = handlePhaseStatus(phaseForms);

              phases.push({
                id: link.phase.id,
                name: link.phase.name,
                position: link.position,
                phaseForms,
                phaseStatus,
              });
            }
          });

          phases.sort((a, b) => a.position - b.position);

          let selectedForm = null;
          if (phases.length > 0) {
            const formId = sessionStorage.getItem("response-visit-form");
            let selPhase = phases?.[0]?.id;

            if (formId) {
              phases.forEach((phase) => {
                const selForm = phase.phaseForms.find((pf) => pf.id === formId);
                if (selForm) {
                  selPhase = phase.id;
                  selectedForm = selForm;
                }
              });
            }
            if (selectedForm) {
              dispatch(getFormData({ selectedForm, studyId }));
            } else {
              phases.some((phase) => {
                const selForm = phase.phaseForms.find(
                  (pf) => pf.status === "active" || pf.status === "inprogress"
                );
                if (selForm) {
                  selPhase = phase.id;
                  selectedForm = selForm;
                }
                return !!selForm;
              });
              if (!selectedForm) {
                selectedForm = phases?.[0]?.phaseForms?.[0];
              }
              if (selectedForm) {
                dispatch(getFormData({ selectedForm, studyId }));
              }
            }
            dispatch(setSelectedPhase({ phase: selPhase }));
          }

          dispatch(setPhaseList({ list: phases }));
          setIsLoading(false);
        }
      } catch (error: any) {
        setIsLoading(false);
        errorToastMessage(error);

        // if (error?.response.data?.metadata?.redirect === "error") {
        //   setError(true);
        // }
      }
    };

    fetchStudyPhases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, dispatch, studyId]);

  useEffect(() => {
    return () => {
      dispatch(resetResponseState());
    };
  }, [dispatch]);

  return (
    <Stack sx={{ height: "100%" }}>
      {/* Navbar */}
      <SurveyHeader setShowDrawer={setShowDrawer} packageName={packageName} />
      <Divider />

      {/* Body */}
      <Stack direction={"row"} gap={2} sx={rootResponses}>
        {/* Phases and Forms */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            height: "100%",
            position: "sticky",
            top: 0,
          }}
        >
          <PhaseList
            packageName={packageName}
            isLoading={isLoading}
            selectedPhaseId={selectedPhase}
            currentTab={"Surveys"}
            showCollapse={false}
          />
        </Box>

        {/* Question List */}
        <QuestionItemWrapper />

        {showDrawer && (
          <MobileDrawer
            showDrawer={showDrawer}
            onClose={() => setShowDrawer(false)}
            packageName={packageName}
          >
            <PhaseList
              packageName={packageName}
              isLoading={isLoading}
              selectedPhaseId={selectedPhase}
              closeMobileDrawer={() => setShowDrawer(false)}
              currentTab={"Surveys"}
              showCollapse={false}
            />
          </MobileDrawer>
        )}
      </Stack>

      <ConfirmationModalWrapper />
      <CalculationWrapper />
    </Stack>
  );
};

export default Survey;
